/**
 * @file Main Application Class
 * @description Central application class that manages modules, lifecycle, and error handling
 */

import { Router } from "./Router.js";
import { EventManager } from "./EventManager.js";

/**
 * Main Application Class
 * Manages the application lifecycle, modules, and error handling
 */
export class App {
  constructor() {
    this.modules = new Map();
    this.router = new Router();
    this.eventManager = EventManager.getInstance();
    this.isInitialized = false;
    this.errorHandler = this.handleError.bind(this);
  }

  /**
   * Initialize the application
   */
  async init() {
    try {
      console.log("🚀 Initializing IPHUB Application...");

      // Setup error handling
      this.setupErrorHandling();

      // Initialize core systems
      await this.router.init();
      await this.eventManager.init();

      // Detect current page/feature
      const currentFeature = this.router.getCurrentFeature();

      // Load and initialize modules based on current feature
      await this.loadModulesForFeature(currentFeature);

      this.isInitialized = true;
      console.log("✅ Application initialized successfully");

      // Emit app ready event
      this.eventManager.emit("app:ready", { feature: currentFeature });
    } catch (error) {
      this.handleError("Application initialization failed", error);
    }
  }

  /**
   * Load modules for a specific feature
   * @param {string} feature - The feature to load modules for
   */
  async loadModulesForFeature(feature) {
    const moduleConfig = this.getModuleConfig(feature);

    for (const [moduleName, config] of Object.entries(moduleConfig)) {
      try {
        const module = await this.loadModule(moduleName, config);
        this.modules.set(moduleName, module);

        // Initialize module if it has an init method
        if (typeof module.init === "function") {
          await module.init();
        }

        console.log(`✅ Loaded module: ${moduleName}`);
      } catch (error) {
        this.handleError(`Failed to load module: ${moduleName}`, error);
      }
    }
  }

  /**
   * Load a single module
   * @param {string} moduleName - Name of the module
   * @param {Object} config - Module configuration
   * @returns {Promise<Object>} The loaded module
   */
  async loadModule(moduleName, config) {
    try {
      const module = await import(config.path);
      return module.default || module;
    } catch (error) {
      throw new Error(`Failed to load module ${moduleName}: ${error.message}`);
    }
  }

  /**
   * Get module configuration for a feature
   * @param {string} feature - The feature name
   * @returns {Object} Module configuration
   */
  getModuleConfig(feature) {
    const configs = {
      "industry-single": {
        singleLocationMap: { path: "./features/single-location-map/index.js" },
        shareLink: { path: "./components/ShareLink.js" },
        // Note: chatBubbles, sliders already initialized in common.js
      },
      "industry-archive": {
        locationList: { path: "./features/location-list/index.js" },
        regionMap: { path: "./features/region/index.js" },
        // Note: chatBubbles, sliders already initialized in common.js
      },
      "fdi-archive": {
        // FDI Map is now loaded via fdi-map.js enqueue - no need for dynamic import
        // Note: chatBubbles, sliders already initialized in common.js
      },
      account: {
        contentToggle: { path: "./components/content-toggle.js" },
        routeStepToggle: { path: "./components/route-step-toggle.js" },
        // Note: accountMenu, chatBubbles already initialized in common.js
      },
      default: {
        // Note: All common modules (notyf, chatBubbles, sliders) are initialized in common.js
        // No need for dynamic imports here
      },
    };

    return configs[feature] || configs.default;
  }

  /**
   * Get a loaded module
   * @param {string} moduleName - Name of the module
   * @returns {Object|null} The module or null if not found
   */
  getModule(moduleName) {
    return this.modules.get(moduleName) || null;
  }

  /**
   * Get current feature
   * @returns {string} Current feature name
   */
  getCurrentFeature() {
    return this.router.getCurrentFeature();
  }

  /**
   * Get loaded modules
   * @returns {Array} Array of loaded module names
   */
  getLoadedModules() {
    return Array.from(this.modules.keys());
  }

  /**
   * Setup error handling
   */
  setupErrorHandling() {
    // Global error handler
    window.addEventListener("error", (event) => {
      this.handleError("Global error", event.error);
    });

    // Unhandled promise rejection handler
    window.addEventListener("unhandledrejection", (event) => {
      this.handleError("Unhandled promise rejection", event.reason);
    });
  }

  /**
   * Handle application errors
   * @param {string} context - Error context
   * @param {Error} error - The error object
   */
  handleError(context, error) {
    console.error(`❌ ${context}:`, error);

    // Emit error event
    this.eventManager.emit("app:error", { context, error });

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.group("🚨 Application Error");
      console.error("Context:", context);
      console.error("Error:", error);
      console.error("Stack:", error.stack);
      console.groupEnd();
    }
  }

  /**
   * Destroy the application
   */
  destroy() {
    try {
      // Destroy all modules
      for (const [moduleName, module] of this.modules) {
        if (typeof module.destroy === "function") {
          module.destroy();
        }
      }

      // Clear modules
      this.modules.clear();

      // Destroy core systems
      this.router.destroy();
      this.eventManager.destroy();

      this.isInitialized = false;
      console.log("🔄 Application destroyed");
    } catch (error) {
      this.handleError("Application destruction failed", error);
    }
  }
}
